import { useEffect, useState } from "react";
import type { Position } from "../lib/geo";
import type { Weather } from "../lib/weather";
import type { ThemeMode } from "../lib/theme";
import { WeatherPanel } from "./Weather";

type Props = {
  position: Position | null;
  weather: Weather | null;
  themeMode: ThemeMode;
  onCycleTheme: () => void;
  onOpenSearch: () => void;
  onToggleDemoRoute: () => void;
  demoRouteActive: boolean;
  satellite: boolean;
  onToggleSatellite: () => void;
  onToggleDebug: () => void;
};

export function TopBar({
  position, weather, themeMode, onCycleTheme, onOpenSearch,
  onToggleDemoRoute, demoRouteActive, satellite, onToggleSatellite, onToggleDebug,
}: Props) {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const id = window.setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(id);
  }, []);

  const hhmm = now.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", hour12: false });
  const date = now.toLocaleDateString(undefined, { weekday: "short", day: "numeric", month: "short" }).toUpperCase();

  // A real GPS fix is present once we have a position that isn't the
  // simulator's synthetic output. When real, the third pill becomes a
  // plain (non-interactive) fix-quality readout — no demo toggle.
  const hasRealFix = !!position && !position.simulated;
  const fix = position
    ? position.simulated
      ? { label: demoRouteActive ? "Demo: routing" : "Demo route", dot: "warn" as const }
      : { label: `${position.accuracy.toFixed(0)} m fix`, dot: "" as const }
    : { label: "Waiting for fix", dot: "off" as const };

  return (
    <div className="top-bar">
      <WeatherPanel weather={weather} />

      <div>
        <div className="clock tabular">{hhmm}</div>
        <div className="clock-meta">{date}</div>
      </div>

      <div className="pill-row">
        <button className="pill pill-primary" onClick={onOpenSearch} aria-label="Søg destination">
          <SearchIcon />
          <span>Hvor til?</span>
        </button>
        <button
          className="pill pill-primary"
          onClick={onCycleTheme}
          aria-label={`Theme: ${themeMode}. Click to cycle.`}
          title={`Theme: ${themeMode}`}
        >
          <ThemeIcon mode={themeMode} />
          <span>{themeMode.toUpperCase()}</span>
        </button>
        <button
          className={`pill pill-primary ${satellite ? "pill-active" : ""}`}
          onClick={onToggleSatellite}
          aria-label={satellite ? "Vis kortvisning" : "Vis satellitvisning"}
          title={satellite ? "Kortvisning" : "Satellitvisning"}
        >
          <LayersIcon />
          <span>{satellite ? "Kort" : "Satellit"}</span>
        </button>
        {hasRealFix ? (
          <button className="pill pill-primary" onClick={onToggleDebug} title="GPS-signal — tryk for debug" aria-label="GPS debug">
            <span className={`dot ${fix.dot}`}></span>
            <span>{fix.label}</span>
          </button>
        ) : (
          <button
            className="pill pill-primary"
            onClick={onToggleDemoRoute}
            aria-label={demoRouteActive ? "Stop demo route" : "Start sample demo route"}
            title={demoRouteActive ? "Stop demo" : "Drive a sample route"}
          >
            <span className={`dot ${fix.dot}`}></span>
            <span>{fix.label}</span>
          </button>
        )}
      </div>
    </div>
  );
}

function SearchIcon() {
  return (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <circle cx="11" cy="11" r="7" />
      <line x1="21" y1="21" x2="16.65" y2="16.65" />
    </svg>
  );
}

function LayersIcon() {
  // Stacked-sheets glyph — the universal "map layers / view" affordance.
  return (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M12 3 2 8l10 5 10-5-10-5z" />
      <path d="M2 13l10 5 10-5" />
      <path d="M2 18l10 5 10-5" opacity="0.55" />
    </svg>
  );
}

function ThemeIcon({ mode }: { mode: ThemeMode }) {
  if (mode === "day") {
    return (
      <svg width="13" height="13" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
        <circle cx="12" cy="12" r="4.5" />
        {[0, 45, 90, 135, 180, 225, 270, 315].map((d) => {
          const r = (d * Math.PI) / 180;
          const x1 = 12 + Math.cos(r) * 7.5;
          const y1 = 12 + Math.sin(r) * 7.5;
          const x2 = 12 + Math.cos(r) * 10;
          const y2 = 12 + Math.sin(r) * 10;
          return <line key={d} x1={x1} y1={y1} x2={x2} y2={y2} stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />;
        })}
      </svg>
    );
  }
  if (mode === "night") {
    return (
      <svg width="13" height="13" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
        <path d="M14 4a8 8 0 1 0 6 14 7 7 0 0 1-6-14z" />
      </svg>
    );
  }
  // auto — half sun/half moon
  return (
    <svg width="13" height="13" viewBox="0 0 24 24" aria-hidden>
      <defs>
        <clipPath id="auto-left"><rect x="0" y="0" width="12" height="24" /></clipPath>
        <clipPath id="auto-right"><rect x="12" y="0" width="12" height="24" /></clipPath>
      </defs>
      <g clipPath="url(#auto-left)">
        <circle cx="12" cy="12" r="4.5" fill="currentColor" />
        {[180, 225, 270, 315].map((d) => {
          const r = (d * Math.PI) / 180;
          const x1 = 12 + Math.cos(r) * 7.5;
          const y1 = 12 + Math.sin(r) * 7.5;
          const x2 = 12 + Math.cos(r) * 10;
          const y2 = 12 + Math.sin(r) * 10;
          return <line key={d} x1={x1} y1={y1} x2={x2} y2={y2} stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />;
        })}
      </g>
      <g clipPath="url(#auto-right)">
        <path d="M14 4a8 8 0 1 0 6 14 7 7 0 0 1-6-14z" fill="currentColor" />
      </g>
    </svg>
  );
}
